import { useEffect, useState, useSyncExternalStore } from "react";

/**
 * Which colour scheme the app shows, on every page including admin and governance.
 * Dusk (the dark theme) is the default, so a device that never chose gets dark,
 * whatever its phone setting says. "system" is a choice a person makes in the
 * switch, and it follows the phone from then on.
 *
 * index.html holds a short copy of this rule that runs before first paint;
 * theme.test.ts runs that copy against resolveTheme so the two cannot drift.
 */
export type ThemePreference = "dark" | "light" | "system";
export type ResolvedTheme = "dark" | "light";

export const THEME_STORAGE_KEY = "hb-theme";
export const DEFAULT_THEME_PREFERENCE: ThemePreference = "dark";

/** The browser chrome colour for each theme: Brand Blue darkened, and Brand Blue. */
export const THEME_COLORS: Record<ResolvedTheme, string> = {
  dark: "#1f293d",
  light: "#37496b",
};

export function parsePreference(raw: string | null): ThemePreference {
  return raw === "dark" || raw === "light" || raw === "system"
    ? raw
    : DEFAULT_THEME_PREFERENCE;
}

export function resolveTheme(input: {
  preference: ThemePreference;
  systemDark: boolean;
}): ResolvedTheme {
  if (input.preference === "system") return input.systemDark ? "dark" : "light";
  return input.preference;
}

const listeners = new Set<() => void>();

/** Holds the choice only when storage refuses writes, so a tap still takes effect. */
let unstoredChoice: ThemePreference | null = null;

export function readPreference(): ThemePreference {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (raw !== null) return parsePreference(raw);
  } catch {
    // Private windows and blocked site data throw; fall through to the choice
    // held in memory, then the default.
  }
  // Storage had nothing, or could not be read. A choice that could not be saved
  // is still the choice the person made.
  return unstoredChoice ?? DEFAULT_THEME_PREFERENCE;
}

export function writePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    unstoredChoice = null;
  } catch {
    unstoredChoice = preference;
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // A change made in another tab reaches this one through the storage event.
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === THEME_STORAGE_KEY) listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

/** The stored preference, and a setter that updates every subscriber at once. */
export function useThemePreference(): [
  ThemePreference,
  (next: ThemePreference) => void,
] {
  const preference = useSyncExternalStore(
    subscribe,
    readPreference,
    () => DEFAULT_THEME_PREFERENCE,
  );
  return [preference, writePreference];
}

const DARK_QUERY = "(prefers-color-scheme: dark)";

/** Whether the phone is set to dark, kept current while the page is open. */
export function useSystemDark(): boolean {
  const [dark, setDark] = useState(
    () => window.matchMedia?.(DARK_QUERY)?.matches ?? false,
  );
  useEffect(() => {
    const query = window.matchMedia?.(DARK_QUERY);
    if (!query) return;
    const onChange = () => setDark(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return dark;
}
